import { QBankStructureLevelCategory } from '@exam-management/graphql';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ExpandMoreRoundedIcon,
  Flex,
  Rotate,
  theme,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';

import { QBankStructureLevelItemRowWithAction } from '../qbank-structure-level-item-row-with-action';

import type { DisplayQBankStructureLevelItem } from './types';

export const QBS_LEVEL_TAG_BG_COLOR_BY_CATEGORY = {
  [QBankStructureLevelCategory.Course]: theme.palette.grey[300],
  [QBankStructureLevelCategory.Part]: theme.palette.grey[700],
  [QBankStructureLevelCategory.Subject]: 'hsla(37, 96%, 59%, 1)',
  [QBankStructureLevelCategory.Group]: 'hsla(40, 97%, 65%, 1)',
  [QBankStructureLevelCategory.Chapter]: 'hsla(42, 97%, 77%, 1)',
  [QBankStructureLevelCategory.Topic]: 'hsla(43, 96%, 89%, 1)',
  [QBankStructureLevelCategory.SubTopic]: 'hsla(42, 100%, 96%, 1)',
};

interface QBankStructureLevelItemProps {
  item: DisplayQBankStructureLevelItem;
  index: number;
}

export function QBankStructureLevelItem(props: Readonly<QBankStructureLevelItemProps>) {
  const { item, index } = props;
  const [isOpen, { toggle }] = useDisclosure(true);

  const { qBankStructureLevel, children } = item;
  const { category } = qBankStructureLevel;

  const isRootLevel = index === 0;
  const hasChildren = children && children.length > 0;
  const expandedBgColor = QBS_LEVEL_TAG_BG_COLOR_BY_CATEGORY[category];

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      expanded={isOpen}
      onChange={toggle}
      sx={{
        overflow: 'hidden',
        height: 'fit-content',
      }}
    >
      <AccordionSummary
        component={Flex}
        expandIcon={
          category !== QBankStructureLevelCategory.SubTopic ? (
            <Rotate in={false}>
              <ExpandMoreRoundedIcon
                sx={(theme) => ({
                  color:
                    category === QBankStructureLevelCategory.Part && isOpen
                      ? theme.palette.common.white
                      : theme.palette.text.primary,
                })}
              />
            </Rotate>
          ) : null
        }
        sx={(theme) => ({
          '&.Mui-focusVisible': {
            backgroundColor: isOpen ? expandedBgColor : theme.palette.common.white,
          },
          '& .MuiAccordionSummary-content': {
            m: theme.spacing(0),
            py: theme.spacing(2),
          },
          p: theme.spacing(0),
          flexDirection: 'row-reverse',
          height: 'fit-content',
          borderBottom: isOpen && hasChildren ? `1px solid ${theme.palette.grey[300]}` : 'none',
          borderLeft: !isRootLevel ? `1px solid ${theme.palette.grey[300]}` : 'none',
          borderTop:
            index > 1 && category !== QBankStructureLevelCategory.Part
              ? `1px solid ${theme.palette.grey[300]}`
              : 'none',
          bgcolor: isOpen ? expandedBgColor : theme.palette.common.white,
          color:
            category === QBankStructureLevelCategory.Part && isOpen
              ? theme.palette.common.white
              : theme.palette.text.primary,
          '& .MuiAccordionSummary-expandIconWrapper': {
            mx: theme.spacing(3),
          },
        })}
      >
        <QBankStructureLevelItemRowWithAction id={item.id} expanded={isOpen} />
      </AccordionSummary>

      {hasChildren ? (
        <AccordionDetails
          sx={(theme) => ({
            padding: theme.spacing(0),
            pl: theme.spacing(4),
            borderLeft: !isRootLevel ? `1px solid ${theme.palette.grey[300]}` : 'none',
            height: 'fit-content',
          })}
        >
          {children?.map((item, index) => (
            <QBankStructureLevelItem key={item.id} item={item} index={index + 1} />
          ))}
        </AccordionDetails>
      ) : null}
    </Accordion>
  );
}
